import CancelablePromise, { cancelable } from 'cancelable-promise';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { analytics } from 'analytics';
import { toaster } from 'ui/Toaster';
import { sankeyFormFlowsArrayToMap } from 'utils/sankey';

import { updateNodesFromFlows } from '../../components/SankeyChart/utils';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { TeamSettings, useTeamContext } from '../team';
import { useCurrentUser } from '../user';
import { isRangeValuesValid, selectionToFlows } from './sankey';

// We currently only support two types of Excel events: selection change and binding data change
type ExcelEventHandlerResult =
  | OfficeExtension.EventHandlerResult<
      Excel.SelectionChangedEventArgs | Excel.BindingDataChangedEventArgs
    >
  | undefined;

/*
 * This hook creates an Excel event handler and cleans it up when the component unmounts,
 * the latter part being the very reason why we have a specialized hook for it.
 * Inspired by: https://learn.microsoft.com/en-us/office/dev/add-ins/excel/excel-add-ins-events#remove-an-event-handler
 */
const useExcelEventHandler = (
  createEventHandler: (
    context: Excel.RequestContext
  ) => Promise<ExcelEventHandlerResult>
) => {
  useEffect(() => {
    let eventHandlerPromise: CancelablePromise<ExcelEventHandlerResult>;
    let eventHandlerResult: ExcelEventHandlerResult;

    // Run the event handler when the component mounts and store the promise and the result
    Excel.run(async (context) => {
      eventHandlerPromise = cancelable(createEventHandler(context));
      eventHandlerResult = await eventHandlerPromise;
    });

    // Clean up the event handler when the component unmounts and cancel the promise
    // in case this effects gets cleaned up before the event handler is created
    return () => {
      if (eventHandlerPromise) {
        eventHandlerPromise.cancel();
      }
      if (eventHandlerResult) {
        Excel.run(eventHandlerResult.context, async (context) => {
          eventHandlerResult?.remove();
          await context.sync();
        });
      }
    };
  }, [createEventHandler]);
};

/*
 * Builds the name of the Excel binding based on the Sankey ID.
 * NOTE: Don't change this as it might break all the existing bindings in production.
 */
const getSankeyExcelBindingName = (sankeyId: number) =>
  `sankeyBinding-${sankeyId}`;

// This is the ID of the toaster that will be shown when the user tries to
// interact with the Excel binding while in cell-editing mode.
const CELL_EDIT_RELOAD_TOASTER_ID = 'cells-editing-mode';

const getExcelBinding = async (
  context: Excel.RequestContext,
  sankeyId: number
) => {
  try {
    const binding = context.workbook.bindings
      .getItemOrNullObject(getSankeyExcelBindingName(sankeyId))
      .load('isNullObject');
    await context.sync();
    return !binding.isNullObject ? binding : undefined;
  } catch (error: any) {
    // If the error is due to the user being in cell-editing mode,
    // we show a toaster with a reload button to let them reload the diagram
    // and apply the latest changes. This is a workaround for the fact that Excel
    // doesn't allow to change bindings while the user is editing a cell.
    if (error.code === 'InvalidOperationInCellEditMode') {
      if (!toaster.isVisible(CELL_EDIT_RELOAD_TOASTER_ID)) {
        toaster.info({
          id: CELL_EDIT_RELOAD_TOASTER_ID,
          title: 'Cell editing detected',
          description: 'Please reload the diagram to apply the latest changes.',
          action: {
            label: 'Reload diagram',
            onClick: () => window.location.reload(),
          },
          duration: Infinity,
        });
      }
      return;
    }
    // Report any other errors to analytics
    analytics.reportError(
      'Error getting Excel binding',
      { error, sankeyId },
      false
    );
  }
};

const getExcelBindingRange = async (
  context: Excel.RequestContext,
  sankeyId: number
) => {
  const binding = await getExcelBinding(context, sankeyId);
  const range = binding?.getRange().load({ address: true, values: true });
  await context.sync();
  return range;
};

const addExcelBindingFromCurrentSelection = async (
  context: Excel.RequestContext,
  sankeyId: number
) => {
  context.workbook.bindings.addFromSelection(
    Excel.BindingType.range,
    getSankeyExcelBindingName(sankeyId)
  );
  await context.sync();
};

const syncDiagramFromBinding = async (
  context: Excel.RequestContext,
  form: SankeyForm,
  binding: Excel.Binding,
  teamSettings: TeamSettings
) => {
  const range = binding.getRange().load({ values: true });
  await context.sync();
  form.setValue(
    'flows',
    sankeyFormFlowsArrayToMap(selectionToFlows(range.values)),
    { shouldDirty: true }
  );
  updateNodesFromFlows(form, teamSettings);
};

/*
 * This hook manages all the Excel binding-related logic for the Sankey diagram.
 * It listens to the Excel events and updates the Sankey form when the user changes the Excel cells' data.
 * It also provides functions to bind the diagram to the current Excel selection and select the Excel cells that are bound to the diagram.
 * NOTE: This hook should be only used inside the SankeyExcelBindingProvider.
 */
export const useSankeyExcelBindingProviderValue = () => {
  const { teamSettings } = useTeamContext();
  const { user } = useCurrentUser();
  const { form } = useSankeyFormContext();
  const sankeyId = form?.watch('id') ?? -1; // TODO: Move away from -1 fallback

  const [bindingRange, setBindingRange] = useState<Excel.Range>();
  const bindingAddress = bindingRange?.address;
  const hasExcelBindingInThisSheet = !!bindingRange;

  const [selectedRange, setSelectedRange] = useState<Excel.Range>();
  const selectedAddress = selectedRange?.address;
  const selectedValues = useMemo(
    // NOTE: Range values are wrongly typed here - they can actually have
    // a null value even though the type definition doesn't allow it.
    () => selectedRange?.values ?? [],
    [selectedRange]
  );

  const isRangeSelectionValid = useMemo(
    () => !!user && selectedValues && isRangeValuesValid(selectedValues),
    [user, selectedValues]
  );

  // Whenever the Sankey ID changes, update its stored binding range
  useEffect(() => {
    Excel.run(async (context) => {
      setBindingRange(undefined);
      setBindingRange(await getExcelBindingRange(context, sankeyId));
    });
  }, [sankeyId]);

  /*
   * This hook listens to the Excel binding data change event and updates the Sankey form when the user changes the Excel cells' data.
   */
  const createBindingDataChangeHandler = useCallback(
    async (context: Excel.RequestContext) => {
      const binding = await getExcelBinding(context, sankeyId);
      if (binding) {
        await syncDiagramFromBinding(context, form, binding, teamSettings);
        return binding.onDataChanged.add(
          (eventArgs: Excel.BindingDataChangedEventArgs) =>
            syncDiagramFromBinding(
              context,
              form,
              eventArgs.binding,
              teamSettings
            )
        );
      }
    },
    [sankeyId, bindingAddress, teamSettings]
  );
  useExcelEventHandler(createBindingDataChangeHandler);

  /*
   * This hook listens to the current workbook's selection change event and updates the stored selected range.
   */
  const createSelectionChangeHandler = useCallback(
    async (context: Excel.RequestContext) => {
      const fetchSelectedRange = async (workbook: Excel.Workbook) => {
        try {
          const range = workbook
            .getSelectedRange()
            .load({ address: true, values: true });
          await workbook.context.sync();
          setSelectedRange(range);
        } catch (e: any) {
          // If the selected range fails for whatever reason,
          // silently pretend no range has been selected
          setSelectedRange(undefined);
        }
      };

      await fetchSelectedRange(context.workbook);
      return context.workbook.onSelectionChanged.add(
        (eventArgs: Excel.SelectionChangedEventArgs) =>
          fetchSelectedRange(eventArgs.workbook)
      );
    },
    []
  );
  useExcelEventHandler(createSelectionChangeHandler);

  /*
   * Adds a persistent Excel binding that will be used to update the Sankey diagram when the user changes the Excel cells' data.
   * This function should be called when the user creates a new Sankey diagram or when the selection changes for an existing diagram.
   * In the latter case, the existing binding will be deleted and a new one will be created in its stead since that's the only
   * way to update the binding's range.
   */
  const bindDiagramToCurrentExcelSelection = useCallback(
    async (sankeyId: number) => {
      try {
        await Excel.run(async (context) => {
          (await getExcelBinding(context, sankeyId))?.delete();
          await addExcelBindingFromCurrentSelection(context, sankeyId);
          setBindingRange(await getExcelBindingRange(context, sankeyId));
        });
      } catch (error) {
        analytics.reportError('Error updating diagram source cells', { error });
      }
    },
    [setBindingRange]
  );

  /*
   * Selects the Excel cells that are bound to the Sankey diagram with provided ID
   */
  const selectDiagramBindingCells = useCallback(async (sankeyId: number) => {
    try {
      await Excel.run(async (context) => {
        (await getExcelBindingRange(context, sankeyId))?.select();
      });
    } catch (error) {
      analytics.reportError('Error selecting diagram source cells', { error });
    }
  }, []);

  return {
    hasExcelBindingInThisSheet,
    bindingAddress,
    selectedAddress,
    selectedValues,
    isRangeSelectionValid,
    bindDiagramToCurrentExcelSelection,
    selectDiagramBindingCells,
  };
};
